import { z } from 'zod';

const REALTIME_VOICE_DELEGATION_MESSAGE_MAX_CHARS = 10_000;

const dispatchIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .optional()
  .describe(
    'Exact dispatch id from an accepted chat handoff or returned task event. Omit for the Your IMAI conversation. Never invent an id.'
  );

const contextScopeSchema = z
  .enum(['source', 'destination'])
  .optional()
  .describe(
    'Use source for the Your IMAI conversation, or destination for the established dispatched task. Omit to continue the current task focus.'
  );

export const getCurrentChatContextSchema = z.object({
  dispatchId: dispatchIdSchema,
  scope: contextScopeSchema,
});

export const findNavigationTargetsSchema = z.object({
  query: z
    .string()
    .trim()
    .min(1)
    .max(256)
    .describe(
      'Concise page name or chat topic keywords, rather than the entire spoken request.'
    ),
  scope: z.enum(['pages', 'chats', 'all']),
  limit: z.number().int().min(1).max(20).optional(),
});

export const navigateUiSchema = z.object({
  kind: z.enum([
    'page',
    'thread',
    'file',
    'brand',
    'product',
    'avatar',
    'route',
    'url',
  ]),
  target: z
    .string()
    .trim()
    .min(1)
    .max(2048)
    .describe(
      'Exact page id or chat reference from discovery, workspace file path, app route, or HTTP(S) URL. For brand, product, or avatar, the exact absolute folder path from workspace_command output. Never invent a chat id or path.'
    ),
});

export const workspaceCommandSchema = z.object({
  command: z
    .string()
    .trim()
    .min(1)
    .max(1000)
    .describe(
      'One read-only workspace command line, for example `ls`, `ls "/acme/users/@sam/Q3 plans"`, `ls --entity product --json`, or `stat <absolute path>`. Paths are relative to the folder the user is viewing.'
    ),
});

export const sendChatMessageSchema = z.object({
  targetThreadReference: z
    .string()
    .trim()
    .min(1)
    .max(2048)
    .describe(
      'Exact accessible chat reference from discovery, the current visible page context, or a verified task event. Resolve titles and topics first; never invent a chat id.'
    ),
  message: z
    .string()
    .trim()
    .min(1)
    .max(REALTIME_VOICE_DELEGATION_MESSAGE_MAX_CHARS)
    .describe(
      'The complete message the user explicitly asked to send, preserving all accepted constraints.'
    ),
});

export const delegationSchema = z.object({
  message: z.string().min(1).max(REALTIME_VOICE_DELEGATION_MESSAGE_MAX_CHARS),
  mode: z
    .enum(['queue', 'interrupt'])
    .default('queue')
    .describe(
      'Delivery mode. Use queue for separate tasks, additive changes, alternatives, variants, or normal follow-ups. Use interrupt only when the user explicitly says the current in-flight request should be stopped, cancelled, replaced, ignored, or corrected instead of queued.'
    ),
  reason: z
    .string()
    .max(800)
    .optional()
    .describe(
      'Brief reason for choosing the delivery mode, especially for interrupt.'
    ),
});

export const quickResearchSchema = z.object({
  query: z
    .string()
    .min(1)
    .max(10_000)
    .describe(
      'A focused, self-contained web research question using the relevant current-chat context.'
    ),
});

export const nativeWebSearchSchema = z.object({
  query: z
    .string()
    .min(1)
    .max(2_000)
    .describe(
      'One focused, self-contained current-information lookup using the relevant current-chat context.'
    ),
});

const answerValueSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('choice'),
    value: z.string().min(1),
    label: z.string().min(1),
  }),
  z.object({
    kind: z.literal('multi_choice'),
    values: z.array(
      z.object({
        value: z.string().min(1),
        label: z.string().min(1),
      })
    ),
  }),
  z.object({ kind: z.literal('confirm'), value: z.boolean() }),
  z.object({ kind: z.literal('number'), value: z.number() }),
  z.object({ kind: z.literal('scale'), value: z.number() }),
  z.object({ kind: z.literal('free_text'), text: z.string().min(1) }),
]);

const groupedAnswerValueSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('choice'),
    value: z.string().min(1),
    label: z.string().min(1),
  }),
  z.object({
    kind: z.literal('multi_choice'),
    values: z.array(
      z.object({
        value: z.string().min(1),
        label: z.string().min(1),
      })
    ),
  }),
  z.object({ kind: z.literal('confirm'), value: z.boolean() }),
  z.object({ kind: z.literal('free_text'), text: z.string().min(1) }),
]);

export const answerCurrentQuestionSchema = z.object({
  dispatchId: dispatchIdSchema,
  scope: contextScopeSchema,
  questionRevision: z
    .string()
    .trim()
    .min(1)
    .max(256)
    .optional()
    .describe(
      'Exact revision from the question that was presented to the user. Never invent a revision or substitute a newer question for the one the user answered.'
    ),
  answerText: z.string().max(4000).optional(),
  toolCallId: z
    .string()
    .optional()
    .describe(
      'Exact current question tool call id from fresh context or a returned question event. Required when answering a dispatched task question.'
    ),
  questionnaireNavigation: z.enum(['previous']).optional(),
  skipCurrentQuestion: z.boolean().optional(),
  answerValue: answerValueSchema.optional(),
  groupAnswers: z.record(z.string(), groupedAnswerValueSchema).optional(),
});

export const voiceCallToolInputSchemas = {
  get_current_chat_context: getCurrentChatContextSchema,
  find_navigation_targets: findNavigationTargetsSchema,
  navigate_ui: navigateUiSchema,
  workspace_command: workspaceCommandSchema,
  send_chat_message: sendChatMessageSchema,
  delegate_to_design_agent: delegationSchema,
  web_search: nativeWebSearchSchema,
  quick_research: quickResearchSchema,
  answer_current_question: answerCurrentQuestionSchema,
};

export function hasValidVoiceCallToolInput(
  name: string,
  input: string
): boolean {
  if (!Object.hasOwn(voiceCallToolInputSchemas, name)) return false;
  try {
    return voiceCallToolInputSchemas[
      name as keyof typeof voiceCallToolInputSchemas
    ].safeParse(JSON.parse(input)).success;
  } catch {
    return false;
  }
}

type AnswerCurrentQuestionInput = z.infer<typeof answerCurrentQuestionSchema>;

export function normalizeGroupedAnswersForConvex(
  answers: AnswerCurrentQuestionInput['groupAnswers']
) {
  if (!answers) return undefined;
  return Object.fromEntries(
    Object.entries(answers).flatMap(([questionId, answer]) => {
      if (answer.kind !== 'free_text') return [[questionId, answer]];

      const text = answer.text.trim();
      if (!text) return [];
      return [
        [
          questionId,
          {
            kind: 'choice' as const,
            value: `custom:${text}`,
            label: text,
          },
        ],
      ];
    })
  );
}

export function normalizeVoiceDelegationReceiptPrompt(prompt: string): string {
  return prompt
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, REALTIME_VOICE_DELEGATION_MESSAGE_MAX_CHARS);
}
